import React, { useContext, useState } from "react";
import * as S from "./style";
import { Text } from "./style";
import { ThemeContext } from "helpers/contexts.js";
import PropTypes from "prop-types";
import Icon from "components/common/Icon";
import * as Constant from "constant";
import { isMobile } from "components/globals";

Chip.propTypes = {
  label: PropTypes.string.isRequired,
  color: PropTypes.oneOf([
    Constant.COLOR.primary,
    Constant.COLOR.secondary,
    Constant.COLOR.success,
    Constant.COLOR.danger,
    Constant.COLOR.badge,
    Constant.COLOR.light,
    Constant.COLOR.dark,
  ]),
  size: PropTypes.oneOf([
    Constant.SIZE.small,
    Constant.SIZE.medium,
    Constant.SIZE.large,
  ]),
  variant: PropTypes.oneOf([
    Constant.VARIENT.contained,
    Constant.VARIENT.outlined,
  ]),
  onClick: PropTypes.func,
  onDeleteButtonClick: PropTypes.func,
  hoverElement: PropTypes.element,
};

Chip.defaultProps = {
  color: Constant.COLOR.primary,
  variant: Constant.VARIENT.contained,
  onClick: null,
  onDeleteButtonClick: null,
  size: Constant.SIZE.xs,
};

function Chip(props) {
  const { colors } = useContext(ThemeContext);
  const direction = document.body.style.direction;
  let stateStyle = getStateStyle(colors)[props.color][props.variant];
  const isDarkStyle = props.color === Constant.COLOR.dark;
  const isLightStyle = props.color === Constant.COLOR.light;
  const isContainedStyle = props.variant === Constant.VARIENT.contained;
  const iconBgColorLightMode = isContainedStyle
    ? colors.gray.gray400
    : colors.gray.iron;
  const iconBgColorDarkMode = isContainedStyle
    ? colors.gray.jumbo
    : colors.gray.gray700 + "30";
  const iconCursorStyle =
    props.onClick || props.onDeleteButtonClick ? "pointer" : "default";
  let hoverStyle = props.onClick
    ? `&:hover{${stateStyle.hover} transition: 0.3s; }`
    : ``;
  let deleteButtonHoverColor = isDarkStyle
    ? iconBgColorDarkMode
    : isLightStyle
    ? iconBgColorLightMode
    : "rgba(255,255,255,0.3)";
  let deleteButtonHoverStyle = props.onDeleteButtonClick
    ? deleteButtonHoverColor
    : "";
  let cursor = props.onClick ? "pointer" : "default";
  let iconButton = null;
  let padding,
    paddingEnd,
    paddingStart,
    fontSize,
    iconStylePadding,
    alignSelf = "",
    marginBetweentextAndIcon = "",
    iconSize = "";
  const [isHovering, setIsHovering] = useState(false);
  const isMobileMode = isMobile();
  const hoverPosition =
    direction === Constant.DIRECTION.rtl ? "right:0" : "left:0";

  let HoverElement;

  if (props.hoverElement) {
    HoverElement = props.hoverElement.type;
  }

  switch (props.size) {
    case Constant.SIZE.xs:
      fontSize = "12px";
      if (props.onDeleteButtonClick) {
        padding = "1px 0px";
        iconStylePadding = "4px 3px";
        paddingStart = "7px";
        paddingEnd = "1px";
        iconSize = Constant.SIZE.xxs;
        marginBetweentextAndIcon = "3px";
      } else {
        padding = "1px 7px";
      }

      break;
    case Constant.SIZE.small:
      fontSize = "14px";
      if (props.onDeleteButtonClick) {
        padding = "1px 0px";
        iconStylePadding = "4px 4px";
        paddingStart = "10px";
        paddingEnd = "1px";
        iconSize = Constant.SIZE.xs;
        marginBetweentextAndIcon = "3px";
      } else {
        padding = "1px 12px";
      }

      break;
    case Constant.SIZE.large:
      fontSize = "16px";
      if (props.onDeleteButtonClick) {
        padding = "2px 3px";
        iconStylePadding = "6px 6px";
        paddingStart = "14px";
        paddingEnd = "1px";
        iconSize = Constant.SIZE.small;
        marginBetweentextAndIcon = "4px";
        alignSelf = "baseline";
      } else {
        padding = "3px 15px";
      }

      break;
    default:
      padding = "3px 10px";
      fontSize = "13px";
      break;
  }

  const handleDeleteClick = (event) => {
    event.stopPropagation();
    props.onDeleteButtonClick();
  };

  const handleOnMouseOver = () => {
    setIsHovering(true);
  };

  const handleOnMouseLeave = () => {
    setIsHovering(false);
  };

  if (props.onDeleteButtonClick) {
    iconButton = (
      <S.IconStyle
        hoverBgColor={deleteButtonHoverStyle}
        padding={iconStylePadding}
        color={props.color}
        onClick={
          props.onDeleteButtonClick && !isMobileMode && handleDeleteClick
        }
        onTouchStart={
          props.onDeleteButtonClick && isMobileMode && handleDeleteClick
        }
        margin={marginBetweentextAndIcon}
        cursor={iconCursorStyle}
      >
        <Icon name={"x-small"} size={iconSize}></Icon>
      </S.IconStyle>
    );
  }

  return (
    <>
      <S.ChipsBase
        baseStyle={stateStyle.base}
        hoverStyle={hoverStyle}
        onMouseOver={handleOnMouseOver}
        onMouseLeave={handleOnMouseLeave}
        onClick={props.onClick}
        cursor={cursor}
        onDeleteButtonClick={props.onDeleteButtonClick}
        padding={padding}
        paddingEnd={paddingEnd}
        paddingStart={paddingStart}
      >
        {HoverElement ? (
          <S.HoverElementWrapper
            isShow={isHovering}
            direction={direction}
            position={hoverPosition}
          >
            <HoverElement {...props.hoverElement.props}></HoverElement>
          </S.HoverElementWrapper>
        ) : (
          ""
        )}
        <Text fontSize={fontSize} alignSelf={alignSelf}>
          {props.label}
        </Text>
        {iconButton}
      </S.ChipsBase>
    </>
  );
}

export default Chip;

function getStateStyle(colors) {
  const stateStyle = {
    primary: {
      contained: {
        base: `color: ${colors.purple.wineBerry};
                background-color: ${colors.purple.lightOrchid};
                border:1px solid transparent;
      `,
        hover: `color: ${colors.white};
                background-color: ${colors.purple.plum};
        `,
      },
      outlined: {
        base: `color: ${colors.purple.plum};
        background-color: transparent;
        border:1px solid ${colors.purple.plum};
      `,
        hover: `color: ${colors.white};
            background-color: ${colors.purple.plum};
      `,
      },
    },

    secondary: {
      contained: {
        base: `color: ${colors.blue.royalBlue};
        background-color: ${colors.blue.royalBlue}10;
        border:1px solid transparent;
      `,
        hover: `color: ${colors.white};
        background-color: ${colors.blue.royalBlue};
        `,
      },
      outlined: {
        base: `color: ${colors.blue.royalBlue};
        background-color: transparent;
        border:1px solid ${colors.blue.royalBlue};
      `,
        hover: `color: ${colors.white};
      background-color: ${colors.blue.royalBlue};
      `,
      },
    },

    success: {
      contained: {
        base: `color: ${colors.green.eucalyptus};
        background-color: ${colors.green.mint};
        border:1px solid transparent;
      `,
        hover: `color: ${colors.white};
        background-color: ${colors.green.darkCyan};
        `,
      },
      outlined: {
        base: `color: ${colors.green.eucalyptus};
        background-color: transparent;
        border:1px solid ${colors.green.darkCyan};
      `,
        hover: `color: ${colors.white};
      background-color: ${colors.green.darkCyan};
      `,
      },
    },

    danger: {
      contained: {
        base: `color: ${colors.red.stiletto};
        background-color: ${colors.red.blush};
        border:1px solid transparent;
      `,
        hover: `color: ${colors.white};
        background-color: ${colors.red.mojo};
        `,
      },
      outlined: {
        base: `color: ${colors.red.stiletto};
        background-color: transparent;
        border:1px solid ${colors.red.mojo};
      `,
        hover: `color: ${colors.white};
      background-color: ${colors.red.mojo};
      `,
      },
    },

    badge: {
      contained: {
        base: `color: ${colors.orange.meteor};
        background-color: ${colors.yellow.creamBrulee};
        border:1px solid transparent;
      `,
        hover: `color: ${colors.white};
        background-color: ${colors.yellow.budhaaGold};
        `,
      },
      outlined: {
        base: `color: ${colors.orange.meteor};
        background-color: transparent;
        border:1px solid ${colors.orange.rajah};
      `,
        hover: `color: ${colors.white};
      background-color: ${colors.yellow.budhaaGold};
      border-color: transparent;
      `,
      },
    },

    light: {
      contained: {
        base: `color: ${colors.gray.charcoal};
        background-color: ${colors.gray.gray200};
        border:1px solid transparent;
      `,
        hover: `color: ${colors.gray.charcoal};
        background-color: ${colors.gray.gray300};
        `,
      },
      outlined: {
        base: `color: ${colors.gray.gray600};
        background-color: transparent;
        border:1px solid ${colors.gray.gray400};
      `,
        hover: `color: ${colors.gray.gray600};
      background-color: ${colors.gray.gray200};
      border-color: ${colors.gray.gray400};
      `,
      },
    },

    dark: {
      contained: {
        base: `color: ${colors.white};
        background-color: ${colors.gray.gray700};
        border:1px solid transparent;
      `,
        hover: `color: ${colors.white};
        background-color: ${colors.gray.charcoal};
        `,
      },
      outlined: {
        base: `color: ${colors.gray.gray700};
        background-color: transparent;
        border:1px solid ${colors.gray.gray700};
      `,
        hover: `color: ${colors.gray.gray700};
      background-color: #D4D4D4;
      border-color: ${colors.gray.gray700};
      `,
      },
    },
  };
  return stateStyle;
}
